import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopPerformers from '../../TopPerformers';
import studentAuthService from '../../../services/studentAuth';
import { ArrowRightOnRectangleIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

// Student-specific API wrapper - handles errors and redirects to student login
const createStudentTestAPI = (navigate) => ({
  getAll: async (skip, limit, includeCompleted) => {
    try {
      // Get available tests for the student
      const tests = await studentAuthService.getAvailableTests();
      return { data: { tests } };
    } catch (error) {
      // If authentication error, redirect to student login
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/student/login');
      }
      // Return empty array on error
      return { data: { tests: [] } };
    }
  },
  getTopPerformers: async (testId) => {
    try {
      const data = await studentAuthService.getTopPerformers(testId);
      return { data };
    } catch (error) {
      // If authentication error, redirect to student login
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/student/login');
      }
      throw error;
    }
  }
});

const StudentTopPerformers = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const studentTestAPI = createStudentTestAPI(navigate);

  useEffect(() => {
    loadStudentInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStudentInfo = async () => {
    try {
      const studentInfo = await studentAuthService.getStudentInfo();
      setStudent(studentInfo);
    } catch (error) {
      console.error('Failed to load student info:', error);
      // Redirect to student login on auth error
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/student/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    studentAuthService.logout();
    navigate('/student/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-lg">
                <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">Student Portal</h1>
                <p className="text-xs sm:text-sm text-gray-400 truncate max-w-[150px] sm:max-w-none">
                  {student?.first_name} {student?.last_name}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all hover:scale-105 text-sm sm:text-base"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Top Performers Component with current student ID */}
      <TopPerformers currentStudentId={student?.student_id} testAPI={studentTestAPI} />
    </div>
  );
};

export default StudentTopPerformers;
